import { AuthContext, ClaimType, formatNumber, Spinner, TooltipEntity, IAuthContextProps } from '@enter/core';
import * as d3 from 'd3';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { IInjectedRootStore } from '../../stores';
import { IChartHierarchy } from './models';
import * as S from './payer-mix-chart.styles';

export interface IPayerMixChartProps {
    startDate: Date;
    endDate: Date;
    claimTypes: ClaimType[];
}
export interface IPayerMixChartState {
    clientRect?: ClientRect;
    activeBox?: IPayerMixBox;
    dotX: number;
    dotY: number;
}
export interface IPayerMixChart {
    startDate: Date;
    endDate: Date;
    claimTypes: ClaimType[];
    payers: IChartHierarchy[];
}
export interface IPayerMixBox {
    i: number;
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    name: string;
    pct: string;
    amt: string;
}

@inject('rootStore')
@observer
export class PayerMixChart extends React.Component<IPayerMixChartProps, IPayerMixChartState> {
    public static contextType = AuthContext;
    private wrapperRef: React.RefObject<HTMLDivElement>;
    private dotRef: React.RefObject<SVGCircleElement>;

    constructor(props: IPayerMixChartProps) {
        super(props);
        this.wrapperRef = React.createRef();
        this.dotRef = React.createRef();
        this.state = {
            dotX: -1,
            dotY: -1,
        };
    }

    public componentDidMount(): void {
        window.addEventListener('resize', this.updateClientRect);
        (this.context as any).setGroup('payer-mix');
        this.updateClientRect();
    }
    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.updateClientRect);
        (this.context as any).cancelGroup('payer-mix');
    }
    public componentDidUpdate(prevProps: IPayerMixChartProps): void {
        // Only update if props actually changed
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate ||
            !_.isEqual(prevProps.claimTypes, this.props.claimTypes)
        ) {
            this.updatePayerMix();
        }
    }
    public render(): React.ReactElement {
        return <S.Wrapper ref={this.wrapperRef}>{this.loading ? <Spinner /> : this.renderChart()}</S.Wrapper>;
    }

    private get injected(): IInjectedRootStore {
        return this.props as IPayerMixChartProps & IInjectedRootStore;
    }
    private get loading(): boolean {
        return _.isEmpty(this.injected.rootStore.metricsStore.payerMixChart);
    }
    private get payerMix(): IPayerMixChart | undefined {
        const payerMixChart = this.injected.rootStore.metricsStore.payerMixChart;
        if (payerMixChart && !_.isError(payerMixChart)) {
            return payerMixChart;
        }
        return undefined;
    }
    private get boxes(): IPayerMixBox[] {
        const { clientRect } = this.state;
        let boxes: IPayerMixBox[] = [];
        if (clientRect && this.payerMix && !_.isEmpty(this.payerMix.payers)) {
            const total = _.sumBy(this.payerMix.payers, 'amount');
            const payersTree = d3
                .stratify<IChartHierarchy>()
                .id(d => d.name)
                .parentId(d => d.parent)(this.payerMix.payers);

            payersTree.sum(d => d.amount).sort((a, b) => b.data.amount - a.data.amount);
            d3.treemap<IChartHierarchy>().size([clientRect.width, clientRect.height])(payersTree as any);
            const leaves: d3.HierarchyRectangularNode<
                IChartHierarchy
            >[] = payersTree.leaves() as d3.HierarchyRectangularNode<IChartHierarchy>[];
            boxes = _.map(leaves, (d, i) => {
                return _.assign(_.pick(d, ['x0', 'x1', 'y0', 'y1']), {
                    i,
                    name: d.data.name,
                    pct: formatNumber('Pct')(d.data.amount / total),
                    amt: formatNumber('Currency')(d.data.amount),
                });
            });
        }
        return boxes;
    }

    private readonly renderChart = (): React.ReactElement | undefined => {
        const { clientRect } = this.state;
        if (clientRect) {
            const { width, height } = clientRect;
            return (
                <S.Chart
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${width} ${height}`}
                    onMouseMove={this.handleMouseMove}
                    onMouseLeave={this.handleMouseLeave}>
                    {_.isEmpty(this.boxes) && this.renderMessage()}
                    {!_.isEmpty(this.boxes) && this.renderBoxes()}
                    {this.renderTooltip()}
                </S.Chart>
            );
        }
    };
    private readonly renderMessage = (): React.ReactElement | undefined => {
        const { clientRect } = this.state;
        if (clientRect) {
            return (
                <S.Message x={clientRect.width / 2} y={clientRect.height / 2}>
                    No data available
                </S.Message>
            );
        }
    };
    private readonly renderBoxes = (): React.ReactElement | undefined => {
        if (this.boxes) {
            const padding = 12;
            const txtWidth = 9;
            const txtHeight = 16;
            const pctHeight = 30;
            const minWidth = 50;
            const boxWidth = (box: IPayerMixBox): number => box.x1 - box.x0;
            const boxHeight = (box: IPayerMixBox): number => box.y1 - box.y0;
            const hasWidth = (box: IPayerMixBox, w: number): boolean => boxWidth(box) > w;
            const hasHeight = (box: IPayerMixBox, h: number): boolean => boxHeight(box) > h;

            const nameY = padding + txtHeight;
            const pctY = padding + txtHeight + padding + pctHeight;
            const amtY = padding + txtHeight + padding + pctHeight + padding + txtHeight;
            const sizeName = (box: IPayerMixBox, name: string): string => {
                const nameWidth = box.name.length * txtWidth;
                const wideEnough = nameWidth <= boxWidth(box);
                if (!hasWidth(box, minWidth) || !hasHeight(box, padding + nameY)) {
                    return '';
                } else if (wideEnough) {
                    return name;
                } else {
                    const chars = boxWidth(box) / txtWidth;
                    return box.name.substring(0, chars) + '…';
                }
            };

            const tiles = _.map(this.boxes, (box, i) => {
                return (
                    <g key={i}>
                        <S.Rect ord={i} x={box.x0} y={box.y0} width={box.x1 - box.x0} height={box.y1 - box.y0} />
                        <S.Text x={box.x0 + padding} y={box.y0 + nameY} className="name">
                            {sizeName(box, box.name)}
                        </S.Text>
                        <S.Pct x={box.x0 + padding} y={box.y0 + pctY} className="pct">
                            {hasWidth(box, minWidth) && hasHeight(box, pctY + padding) ? box.pct : ''}
                        </S.Pct>
                        <S.Text x={box.x0 + padding} y={box.y0 + amtY} className="amt">
                            {hasWidth(box, minWidth) && hasHeight(box, pctY + padding) ? box.amt : ''}
                        </S.Text>
                    </g>
                );
            });
            return <g className="treeMap">{tiles}</g>;
        }
    };
    private readonly renderTooltip = (): React.ReactElement | undefined => {
        const { activeBox, dotX, dotY } = this.state;
        if (activeBox && dotX !== -1 && dotY !== -1) {
            return (
                <>
                    <circle r={1} cx={dotX} cy={dotY} fill="transparent" ref={this.dotRef} />
                    <TooltipEntity open={dotX !== -1 && dotY !== -1} direction="top" anchor={this.dotRef} size="large">
                        <S.Tooltip>
                            <S.TooltipTitle ord={activeBox.i}>{activeBox.name}</S.TooltipTitle>
                            <S.TooltipRow>
                                <S.TooltipLeft>Total charges</S.TooltipLeft>
                                <S.TooltipRight>{activeBox.amt}</S.TooltipRight>
                            </S.TooltipRow>
                            <S.TooltipRow>
                                <S.TooltipLeft>Share</S.TooltipLeft>
                                <S.TooltipRight>{activeBox.pct}</S.TooltipRight>
                            </S.TooltipRow>
                        </S.Tooltip>
                    </TooltipEntity>
                </>
            );
        }
    };
    private readonly handleMouseMove = (e: React.MouseEvent): void => {
        const { clientRect } = this.state;
        if (clientRect && this.payerMix) {
            const mouseX = e.clientX - clientRect.left;
            const mouseY = e.clientY - clientRect.top;
            let dotX = -1;
            let dotY = -1;
            const activeBox = _.find(
                this.boxes,
                box => box.x0 <= mouseX && mouseX <= box.x1 && box.y0 <= mouseY && mouseY <= box.y1,
            );
            if (activeBox) {
                dotX = activeBox.x0 + (activeBox.x1 - activeBox.x0) / 2;
                dotY = activeBox.y0;
            }
            this.setState({
                activeBox,
                dotX,
                dotY,
            });
        }
    };
    private readonly handleMouseLeave = (e: React.MouseEvent): void => {
        this.setState({
            dotX: -1,
            dotY: -1,
            activeBox: undefined,
        });
    };
    private readonly updatePayerMix = (): void => {
        if (
            !this.payerMix ||
            this.payerMix.startDate !== this.props.startDate ||
            this.payerMix.endDate !== this.props.endDate ||
            !_.isEqual(this.payerMix.claimTypes, this.props.claimTypes)
        ) {
            this.injected.rootStore.metricsStore.loadPayerMixChart(
                this.props.startDate,
                this.props.endDate,
                this.props.claimTypes,
            );
        }
    };
    private readonly updateClientRect = (): void => {
        if (this.wrapperRef && this.wrapperRef.current) {
            const clientRect = this.wrapperRef.current.getBoundingClientRect();
            if (
                !this.state.clientRect ||
                clientRect.top !== this.state.clientRect.top ||
                clientRect.left !== this.state.clientRect.left ||
                clientRect.height !== this.state.clientRect.height ||
                clientRect.width !== this.state.clientRect.width
            ) {
                this.setState({
                    clientRect,
                });
            }
        }
    };
}
